import React from 'react';

import type {ICategoryActions} from 'coreHooks/categories';

import type {IPersonalizedItemProps} from 'brand/app/contentful/components/PersonalizedBlock/IPersonalizedBlock';

import type {TRotationalBannerData, TRotationalBannerSlide} from './IRotationalBanner';

interface IRichTextMark {
	type: string;
}

interface IRichTextNode {
	nodeType: string;
	value?: string;
	marks?: IRichTextMark[];
	data?: {
		uri?: string;
		target?: {
			targetUrl?: string;
			targetPage?: {props?: {slug?: string}};
			targetCategory?: string;
		};
	};
	content?: IRichTextNode[];
}

export const parseTexts = (
	item: IPersonalizedItemProps | null | undefined,
	categoryActions?: Pick<ICategoryActions, 'getCategory'>,
): TRotationalBannerData => {
	const textTemp: TRotationalBannerData = [];

	if (!item) {
		return textTemp;
	}

	const richTextContent = (item.item as {content?: {json?: {content?: IRichTextNode[]}}})?.content?.json?.content;

	richTextContent?.forEach((element, mainIndex) => {
		if (element.nodeType === 'unordered-list' || element.nodeType === 'paragraph') {
			element.content?.forEach((content) => {
				if (content.nodeType === 'list-item') {
					content.content?.forEach((listItem, subIndex) => {
						const listItemContent = listItem.content;
						const innerItem: React.ReactNode[] = [];

						listItemContent?.forEach((elem, key) => {
							const parsed = parseElement(elem, `${subIndex}-${key}`, categoryActions);

							if (parsed) {
								innerItem.push(parsed);
							}
						});

						textTemp.push(innerItem);
					});
				} else {
					const parsed = parseElement(content, mainIndex, categoryActions);

					if (parsed) {
						textTemp.push(parsed);
					}
				}
			});
		}
	});

	return textTemp;
};

const parseElement = (
	element: IRichTextNode,
	key: string | number,
	categoryActions?: Pick<ICategoryActions, 'getCategory'>,
): TRotationalBannerSlide | null => {
	if (((element.nodeType === 'hyperlink' && element?.data?.uri)
		|| (element.nodeType === 'entry-hyperlink'))
		&& element.content) {
		const contents = element.content;

		if (contents.length) {
			const tempText: React.ReactNode[] = [];

			for (const content of contents) {
				if (content.value) {
					tempText.push(applyMarks(content.value, content.marks));
				}
			}

			if (tempText.length) {
				let href = '';

				if (element.nodeType === 'hyperlink') {
					href = element?.data?.uri ?? '';
				} else {
					const target = element?.data?.target;

					href = target?.targetUrl
						|| target?.targetPage?.props?.slug
						|| (target?.targetCategory && categoryActions?.getCategory(target.targetCategory)?.getCategoryUrl())
						|| '';
				}

				return (
					<a href={href} key={key}>
						{tempText}
					</a>
				);
			}
		}
	}

	if (element.nodeType === 'text' && element.value !== '') {
		return applyMarks(element.value, element.marks, key);
	}

	return null;
};

const applyMarks = (
	value: string,
	marks?: IRichTextMark[],
	key: string | number | null = null,
): React.ReactNode => {
	if (!marks || !marks.length) {
		return value;
	}

	let tempText = value;

	marks.forEach((mark) => {
		switch (mark.type) {
			case 'bold':
				tempText = `<b>${tempText}</b>`;

				break;
			case 'italic':
				tempText = `<i>${tempText}</i>`;

				break;
		}
	});

	return <span key={key} dangerouslySetInnerHTML={{__html: tempText}}/>;
};
